import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { CgMoveRight } from 'react-icons/cg';
import { useParams } from 'react-router-dom';

import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import VideoPreview from '../../../components/VideoPreview';
import DrawerMenu from '../../../components/DrawerMenu';
import UserInfoHeader from '../UserInfoHeader';

import {
  PageWrapper,
  LPText,
  NavBar,
  CardArea,
  CardBody,
  CardContent,
  CardFooter,
  ScrollingContent,
} from '../../styles';
import {
  FormControl,
  HomeButtonWrapper,
  PurpleSwitch,
  BackButtonWrapper,
  useStyles,
} from './styles';

const PageLayout = ({
  isDark,
  hasBackBtn,
  onBackBtnClick,
  coverMedia,
  showFooter,
  showUserInfo,
  account,
  footer,
  children,
}) => {
  const scrollArea = useRef();
  const scrollProgress = useRef(0);
  const classes = useStyles();
  const hasCoverMedia = !!coverMedia;
  const [isDarkMode, setIsDarkMode] = useState(isDark);
  const [checked, setChecked] = React.useState(false);
  const [progress, setProgress] = useState(0);
  const [openMenu, setOpenMenu] = useState(false);

  const onScroll = useCallback(
    (event) => {
      scrollProgress.current = (event.currentTarget.scrollTop * 100) / 200;
      scrollProgress.current = scrollProgress.current > 100 ? 100 : scrollProgress.current;
      scrollProgress.current = scrollProgress.current < 0 ? 0 : scrollProgress.current;

      if (scrollProgress.current !== progress) {
        setProgress(scrollProgress.current);
      }
    },
    [progress],
  );

  const toggleMenu = () => {
    setOpenMenu(!openMenu);
  };

  const { virtualPhoneNumber } = useParams();

  useEffect(() => {
    scrollArea.current.addEventListener('scroll', onScroll);
    return () => scrollArea.current && scrollArea.current.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsDarkMode(isDark);
  }, [isDark]);

  const toggleChecked = () => {
    setIsDarkMode(!isDarkMode);
    setChecked((prev) => !prev);
  };

  return (
    <PageWrapper dark={isDarkMode}>
      <CardArea>
        <DrawerMenu
          dark={isDarkMode}
          virtualPhoneNumber={virtualPhoneNumber}
          state={openMenu}
          toggleMenu={toggleMenu}
        />
        <NavBar>
          <LPText marginright small w400>
            {!hasBackBtn && (
              <HomeButtonWrapper isDark={isDarkMode} onClick={() => toggleMenu()}>
                <CgMoveRight />
              </HomeButtonWrapper>
            )}
            {hasBackBtn && (
              <BackButtonWrapper onClick={onBackBtnClick}>
                <ArrowBackIosIcon className={classes.backButton} />
                <span>BACK</span>
              </BackButtonWrapper>
            )}
          </LPText>

          <a href="https://snapcall.me/">
            <img
              src={isDarkMode ? '/images/sc_white_logo.png' : '/images/logo-dark-new.png'}
              alt="Snapcall Logo"
              className="sc_logo"
            />
          </a>

          <LPText marginleft>
            <FormControl
              control={<PurpleSwitch size="small" checked={checked} onChange={toggleChecked} />}
              label={isDarkMode ? 'Dark' : 'Light'}
              labelPlacement="bottom"
            />
          </LPText>
        </NavBar>
        <CardBody>
          <ScrollingContent ref={scrollArea} nopadding>
            {hasCoverMedia && <VideoPreview urlMetadata={coverMedia.uri} progress={progress} />}

            {showUserInfo && (
              <UserInfoHeader
                sticky
                avatarImg={account.avatarImg}
                firstName={account.firstName}
                lastName={account.lastName}
                description={account.description}
                isVerified={account.isVerified}
                extraText={account.extraText}
                progress={hasCoverMedia ? progress : 100}
              />
            )}
            <CardContent>{children}</CardContent>
          </ScrollingContent>
        </CardBody>

        {showFooter && <CardFooter>{footer}</CardFooter>}
      </CardArea>
    </PageWrapper>
  );
};

PageLayout.defaultProps = {
  coverMedia: null,
  showFooter: false,
  isDark: false,
  hasBackBtn: false,
  showUserInfo: false,
  account: {},
  onBackBtnClick: () => true,
  footer: <div />,
};

PageLayout.propTypes = {
  hasBackBtn: PropTypes.bool,
  onBackBtnClick: PropTypes.func,
  coverMedia: PropTypes.shape(),
  showFooter: PropTypes.bool,
  isDark: PropTypes.bool,
  showUserInfo: PropTypes.bool,
  account: PropTypes.shape(),
  footer: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export default PageLayout;
